import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productAPI } from '../../services/productAPI';

interface ProductState {
  products: any[];
  selectedProduct: any;
  totalProducts: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  products: [],
  selectedProduct: null,
  totalProducts: 0,
  currentPage: 1,
  totalPages: 0,
  isLoading: false,
  error: null,
};

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params: { page?: number; limit?: number; search?: string; categoryId?: string; minPrice?: string; maxPrice?: string }) => {
    return await productAPI.getAllProducts(params);
  }
);

export const fetchProductDetails = createAsyncThunk(
  'products/fetchProductDetails',
  async (productId: string) => {
    return await productAPI.getProductById(productId);
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async ({ productData, categoryId }: { productData: FormData; categoryId: string }) => {
    return await productAPI.createProduct(productData, categoryId);
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, data }: { id: string; data: FormData }) => {
    return await productAPI.updateProduct(id, data);
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (productId: string) => {
    await productAPI.deleteProduct(productId);
    return productId;
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        console.log('Products API Response:', action.payload);
        state.isLoading = false;
        state.products = action.payload.data || [];
        state.totalProducts = action.payload.totalProducts || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.unshift(action.payload.data || action.payload);
        state.totalProducts += 1;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const updatedProduct = action.payload.data || action.payload;
        const index = state.products.findIndex(p => p._id === updatedProduct._id);
        if (index !== -1) {
          state.products[index] = updatedProduct;
        }
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p._id !== action.payload);
        state.totalProducts -= 1;
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.selectedProduct = action.payload.data;
      });
  },
});

export const { clearError } = productSlice.actions;
export default productSlice.reducer;